import React, { useState, useEffect, useCallback } from 'react';
import { ReactComponent as CheckmarkIcon } from 'assets/icons/svg/rebate-checkmark.svg';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import { showToast } from 'components/atomic/Toast/Toast';
import { useGlobal } from 'context/global/GlobalContext';
import { fetchWrapper } from 'utils/previewMockCalls';
import { useTranslation } from 'react-i18next';
import { uploadToS3 } from 'utils/uploadToS3';
import { IFileFrom } from 'types/FileType';
import LoadingIndicator from 'components/atomic/LoadingIndicator';
import FileUpload from 'components/atomic/FileUpload';
import envVars from 'env-vars';

type RebateStatusData = {
  status:
    | 'initiation'
    | 'submission'
    | 'denial'
    | 'approval'
    | 'completion'
    | 'payout';
  message: string;
  denialMessage?: string;
  statusHistory?: {
    status: string;
    timestamp: string;
  }[];
};

const hollowIcon = (
  isCurrentStep: boolean,
  currentStatus?: string,
  stateStatus?: string
) => {
  const isApprovalStep = stateStatus === 'approval';
  const isCompletionStep = stateStatus === 'completion';
  const showLoading =
    isCurrentStep &&
    ((isApprovalStep &&
      (currentStatus === 'submission' || currentStatus === 'approval')) ||
      (isCompletionStep && currentStatus === 'approval') ||
      currentStatus === 'completion' ||
      currentStatus === 'payout');

  const showColoredBorder =
    isCurrentStep &&
    (currentStatus === 'initiation' ||
      currentStatus === 'denial' ||
      currentStatus === 'submission');

  return showLoading ? (
    <LoadingIndicator
      speed={1.2}
      styles='!w-[28px] !h-[28px] -translate-x-0.5 translate-y-px'
    />
  ) : (
    <div
      className={`relative w-6 h-6 mt-1 aspect-square rounded-full border-solid border-[2.5px] ${
        showColoredBorder ? 'border-primary' : 'border-discountFrame'
      }`}
    />
  );
};

const RebateStatus: React.FC = () => {
  const [fileUploading, setFileUploading] = useState<boolean>(false);
  const [loadingRebateStatus, setLoadingRebateStatus] = useState<boolean>(true);
  const [receiptUploadedSuccessfully, setReceiptUploadedSuccessfully] =
    useState<boolean>(false);
  const [rebateStatusData, setRebateStatusData] = useState<RebateStatusData>({
    status: 'initiation',
    message: '',
  });
  const { brandTheme } = useThemeContext();
  const { token } = useGlobal();
  const { t } = useTranslation('translation', { keyPrefix: 'purchaseDetails' });
  const { t: statusTranslations } = useTranslation('translation', {
    keyPrefix: 'smsOptIn.rebateStatus',
  });

  const fetchRebateStatus = useCallback(async () => {
    const queryParams = new URLSearchParams(window.location.search);
    const rebateId = queryParams.get('rebateId');

    if (!rebateId) {
      setLoadingRebateStatus(false);
      return;
    }

    const API_URL =
      envVars.REACT_APP_STAND_ALONE_MODE === 'false'
        ? envVars.REACT_APP_API_URL
        : '';

    try {
      const response = await fetchWrapper(
        `${API_URL}/shared-api/rebate/status?rebateId=${rebateId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        try {
          const data = await response.json();
          setRebateStatusData(data);
        } catch (jsonError: any) {
          console.error('Error parsing JSON:', jsonError.message);
          return;
        }
      } else {
        console.error('Error:', response.statusText);
        return;
      }
    } catch (e: any) {
      console.error('Error:', e.message);
    } finally {
      setLoadingRebateStatus(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRebateStatus();
  }, [fetchRebateStatus]);

  const changeSelectedFile = async (files: FileList | null) => {
    if (!files?.length) {
      setFileUploading(false);
      return;
    }
    const uploaded_files = [...files];
    setFileUploading(true);
    const uploadFilePromiseArr = [];

    for (let i = 0; i < uploaded_files.length; i++) {
      if (
        ![
          'application/pdf',
          'image/png',
          'image/jpg',
          'image/jpeg',
          'image/heif',
          'image/heic',
        ].includes(uploaded_files[i].type)
      ) {
        showToast({ message: t('invalidType'), type: 'error' });
        setFileUploading(false);
        return;
      }

      let fileObject: IFileFrom = {
        fileName: uploaded_files[i]?.name,
        fileUrl: '',
      };

      uploadFilePromiseArr.push(async () => {
        try {
          const url = await uploadToS3(uploaded_files[i]);
          return {
            ...fileObject,
            fileUrl: url,
          };
        } catch (e) {
          throw e;
        }
      });
    }

    if (uploadFilePromiseArr.length) {
      try {
        const results = await Promise.all(uploadFilePromiseArr.map((p) => p()));
        await submitReceipt(results);
      } catch (e) {
        console.error('Error during file upload:', e);
        showToast({ message: t('receiptUploadError'), type: 'error' });
        setFileUploading(false);
        return;
      }
    } else {
      setFileUploading(false);
    }
  };

  const submitReceipt = async (files: IFileFrom[]) => {
    if (!files?.length) {
      setFileUploading(false);
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const rebateId = queryParams.get('rebateId');

    if (!rebateId) {
      console.error('No rebateId found in URL');
      showToast({ message: t('receiptUploadError'), type: 'error' });
      setFileUploading(false);
      return;
    }

    const API_URL =
      envVars.REACT_APP_STAND_ALONE_MODE === 'false'
        ? envVars.REACT_APP_API_URL
        : '';

    const receiptUrl = files.find((file) => !!file.fileUrl)?.fileUrl;

    if (!receiptUrl) {
      console.error('No receiptUrl found in files');
      showToast({ message: t('receiptUploadError'), type: 'error' });
      setFileUploading(false);
      return;
    }

    try {
      const response = await fetchWrapper(
        `${API_URL}/shared-api/rebate/email-rebate-receipt-submission`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            rebateId: rebateId,
            receiptUrl: receiptUrl,
          }),
        }
      );

      if (response.ok) {
        try {
          await response.json();
        } catch (jsonError) {
          // No JSON response body, but request was successful
        }
        showToast({ message: t('receiptUploadSuccess'), type: 'success' });
        setReceiptUploadedSuccessfully(true);
        setFileUploading(false);
        await fetchRebateStatus();
      } else {
        const errorData = await response
          .json()
          .catch(() => ({ message: response.statusText }));
        console.error('Receipt submission failed:', response.status, errorData);
        showToast({ message: t('receiptUploadError'), type: 'error' });
        setFileUploading(false);
      }
    } catch (e) {
      console.error('Error submitting receipt:', e);
      showToast({ message: t('receiptUploadError'), type: 'error' });
      setFileUploading(false);
    }
  };

  return (
    <div className='relative w-full h-auto flex flex-col items-start justify-center border-solid border border-gray-200 bg-white rounded-[26px] shadow-sm p-6 gap-6'>
      <div className='relative w-full flex flex-col items-center justify-center pt-2 gap-2.5'>
        <h3 className='font-semibold text-lg text-grayBlack'>
          {statusTranslations('heading')}
        </h3>
        {rebateStatusData.status !== 'approval' &&
          rebateStatusData.status !== 'completion' &&
          rebateStatusData.status !== 'payout' && (
            <p className='text-center font-normal text-xs text-[#3D4152]'>
              {statusTranslations('helpText')}
            </p>
          )}
      </div>
      <div className='w-full h-px bg-lightBorder' />

      {loadingRebateStatus ? (
        <div className='relative w-full flex items-center justify-center py-8'>
          <LoadingIndicator size='50px' />
        </div>
      ) : (
        <ul className='relative w-full flex flex-col items-center justify-center gap-3 list-style-none'>
          {(() => {
            const states = rebateStates(statusTranslations);
            return states.map((state, index) => {
              const getCurrentStatusIndex = () => {
                const status = rebateStatusData.status;
                if (status === 'initiation') return 1;
                if (status === 'submission') return 2;
                if (status === 'denial') return 1;
                if (status === 'approval') return 3;
                if (status === 'completion' || status === 'payout') return 3;
                return 0;
              };

              const currentStatusIndex = getCurrentStatusIndex();
              const isCurrentStep = index === currentStatusIndex;
              const isCompletionOrPayout =
                rebateStatusData.status === 'completion' ||
                rebateStatusData.status === 'payout';

              const showCheckmark =
                index < currentStatusIndex ||
                (isCurrentStep && isCompletionOrPayout);

              const isLastItem = index === states.length - 1;
              const isCompletedOrActive = showCheckmark || isCurrentStep;
              const lineColor = isCompletedOrActive
                ? 'bg-primary'
                : 'bg-discountFrame';

              const isSubmissionState = state.status === 'submission';
              const isDenied = rebateStatusData?.status === 'denial';
              const showMessage =
                isCurrentStep && isSubmissionState && isDenied;
              const showUpload =
                isSubmissionState &&
                isCurrentStep &&
                (rebateStatusData.status === 'initiation' ||
                  rebateStatusData.status === 'submission' ||
                  rebateStatusData.status === 'denial') &&
                !receiptUploadedSuccessfully;

              const getStatusHistoryEntry = () => {
                if (
                  !rebateStatusData.statusHistory ||
                  rebateStatusData.statusHistory.length === 0
                )
                  return null;
                const statusToMatch =
                  state.status === 'completion'
                    ? ['completion', 'payout']
                    : [state.status];
                return rebateStatusData.statusHistory.find((entry) =>
                  statusToMatch.includes(entry.status)
                );
              };

              const statusHistoryEntry = getStatusHistoryEntry();
              const formatTimestamp = (timestamp: string) => {
                try {
                  const date = new Date(timestamp);
                  return date.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true,
                  });
                } catch (e) {
                  return timestamp;
                }
              };

              const getCompletedText = () => {
                let baseText: string;
                if (state.status === 'approval') {
                  baseText = statusTranslations('approved');
                } else if (state.status === 'completion') {
                  baseText = statusTranslations('paid');
                } else {
                  baseText = statusTranslations('completed');
                }
                if (statusHistoryEntry?.timestamp)
                  return `${baseText} ${formatTimestamp(
                    statusHistoryEntry.timestamp
                  )}`;
                return baseText;
              };

              const getDeniedText = () => {
                const denialHistoryEntry = rebateStatusData.statusHistory?.find(
                  (entry) => entry.status === 'denial'
                );

                const rejectedText = statusTranslations('rejected');
                const timestampText = denialHistoryEntry?.timestamp
                  ? formatTimestamp(denialHistoryEntry.timestamp)
                  : '';
                const reasonText = statusTranslations('forTheFollowingReason');
                const rawMessageText = rebateStatusData.denialMessage || '';
                const messageText = rawMessageText
                  ? rawMessageText.charAt(0).toUpperCase() +
                    rawMessageText.slice(1)
                  : '';
                const resubmitText = statusTranslations('pleaseResubmit');

                let deniedMessage = rejectedText;
                if (timestampText) deniedMessage += ` ${timestampText}`;
                if (messageText)
                  deniedMessage += ` ${reasonText} "${messageText}".`;

                deniedMessage += ` ${resubmitText}`;
                return deniedMessage;
              };

              return (
                <li
                  key={state.status}
                  className='relative w-full flex flex-row items-start justify-start pl-2 gap-1.5'
                >
                  <div
                    className='relative h-full flex-shrink-0'
                    style={{ width: '24px' }}
                  >
                    {showCheckmark ? (
                      <CheckmarkIcon
                        width={25}
                        height={25}
                        fill={brandTheme}
                        className='mt-[3px] relative z-10'
                      />
                    ) : (
                      <div className='relative z-10'>
                        {hollowIcon(
                          index === currentStatusIndex,
                          rebateStatusData.status,
                          state.status
                        )}
                      </div>
                    )}
                    {!isLastItem && showCheckmark && (
                      <div
                        className={`absolute left-1/2 -translate-x-1/2 w-0.5 ${lineColor}`}
                        style={{
                          top: '29px',
                          bottom: '-16px',
                        }}
                      />
                    )}
                  </div>
                  <div className='h-full flex flex-col items-start flex-1 min-w-0'>
                    <p
                      className={`text-xs font-bold ${
                        !(showCheckmark || isCurrentStep) && !showUpload
                          ? 'py-2'
                          : ''
                      } ${showCheckmark ? 'text-primary' : 'text-grayBlack'}`}
                    >
                      {state.heading}
                    </p>
                    {(showCheckmark || isCurrentStep) && (
                      <p
                        className={`text-xxs break-words max-w-full ${
                          isSubmissionState && isDenied
                            ? 'text-warning'
                            : 'text-grayBlack'
                        }`}
                      >
                        {showCheckmark
                          ? getCompletedText()
                          : showMessage
                          ? getDeniedText()
                          : state.helpText}
                      </p>
                    )}
                    {showUpload && (
                      <div className='w-full mt-3'>
                        {fileUploading ? (
                          <div className='ml-[54px] w-max flex items-center justify-center'>
                            <LoadingIndicator size='36px' />
                          </div>
                        ) : (
                          <FileUpload
                            compact={true}
                            multiple={false}
                            loading={false}
                            setSelectedFiles={() => {}}
                            changeFile={changeSelectedFile}
                            uploadButtonText={t('uploadReceiptButtonText')}
                            selectedFiles={undefined}
                            validatePdf
                          />
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            });
          })()}
        </ul>
      )}
    </div>
  );
};

export default RebateStatus;

const rebateStates = (statusTranslations: (key: string) => string) => [
  {
    status: 'initiation',
    heading: statusTranslations('rebateSignup'),
    helpText: statusTranslations('completed'),
  },
  {
    status: 'submission',
    heading: statusTranslations('rebateSubmission'),
    helpText: statusTranslations('awaitingReceipt'),
  },
  {
    status: 'approval',
    heading: statusTranslations('rebateApproval'),
    helpText: statusTranslations('awaitingApproval'),
  },
  {
    status: 'completion',
    heading: statusTranslations('rebatePayout'),
    helpText: statusTranslations('rebatePayoutHelpText'),
  },
];
